import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import PageSkeleton from '../../../components/PageSkeleton';
import TerminalPricePulseTable from './TerminalPricePulseTable';
import {
  useTerminalPricePulseMeta,
  useTerminalPricePulseMultiWeekData,
} from '../../../utils/api-hooks/analytics';
import Colors from '../../../constants/Colors';
import { TERMINAL_PULSE_FIND_ALL } from './terminalPricePulseConstants';
import { lastNIsoWeeks, weekKey } from './terminalPricePulseTableUtils';

const centerClass =
  'flex h-full min-h-0 w-full min-w-0 flex-col items-center justify-center gap-3 px-4';

/**
 * Self-contained container for Terminal Price Pulse table.
 * Uses weekly ISO endpoint — does not pass dashboard date_start / date_end.
 */
const TerminalPricePulseTableContainer = ({ apiParams, ...restProps }) => {
  const { t } = useTranslation();
  const { email, commodity } = apiParams || {};
  const [find, setFind] = useState(TERMINAL_PULSE_FIND_ALL);
  const [selectedWeeks, setSelectedWeeks] = useState([]);
  const weeksInitRef = useRef(false);

  useEffect(() => {
    weeksInitRef.current = false;
    setSelectedWeeks([]);
  }, [commodity]);

  const {
    availableWeeks,
    findLabel: metaFindLabel,
    isLoading: metaLoading,
    isFetching: metaFetching,
    isError: metaError,
    error: metaErr,
  } = useTerminalPricePulseMeta(email, commodity, find);

  useEffect(() => {
    if (weeksInitRef.current || !availableWeeks.length) return;
    setSelectedWeeks(lastNIsoWeeks(availableWeeks, 4));
    weeksInitRef.current = true;
  }, [availableWeeks]);

  const {
    pulseRows,
    isLoading: rowsLoading,
    isFetching: rowsFetching,
    isError: rowsError,
    error: rowsErr,
  } = useTerminalPricePulseMultiWeekData(email, commodity, find, selectedWeeks);

  const isLoading = metaLoading || (selectedWeeks.length > 0 && rowsLoading);
  const isFetching = metaFetching || rowsFetching;
  const isError = metaError || rowsError;
  const error = metaErr || rowsErr;

  const weekOptions = useMemo(
    () =>
      availableWeeks.map((w) => ({
        value: weekKey(w),
        label: w.week_label || `Week ${w.iso_week} – ${w.iso_year}`,
        week: w,
      })),
    [availableWeeks]
  );

  if (!email || !commodity) {
    return (
      <div className={centerClass}>
        <p className="text-center text-sm text-gray-400">{t('analytics.selectCommodity')}</p>
      </div>
    );
  }

  if (isLoading && !availableWeeks.length && !pulseRows.length) {
    return <PageSkeleton shape="chart" />;
  }

  if (isError) {
    return (
      <div className={centerClass}>
        <AlertCircle size={32} style={{ color: Colors.error || '#ef4444' }} />
        <p className="text-center text-sm text-gray-400">{error?.message || t('charts.errorLoading')}</p>
      </div>
    );
  }

  return (
    <TerminalPricePulseTable
      data={pulseRows}
      commodity={commodity}
      find={find}
      findLabel={metaFindLabel || ''}
      onFindChange={setFind}
      selectedWeekKeys={selectedWeeks.map(weekKey)}
      onWeekKeysChange={(keys) => {
        const next = keys
          .map((k) => weekOptions.find((o) => o.value === k)?.week)
          .filter(Boolean);
        setSelectedWeeks(next);
      }}
      weekOptions={weekOptions}
      isFetching={isFetching}
      {...restProps}
    />
  );
};

export default TerminalPricePulseTableContainer;
